import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Search, Edit3, Trash2, Volume2, ChevronLeft, ChevronRight, UserRound,
} from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { LeadForm } from './LeadForm'
import { useVociElevenLabs } from '../../hooks/useVociElevenLabs'
import { etichettaDifficolta, varianteDifficolta } from '../../lib/format'
import type { LeadAdmin } from '../../lib/tipi'
import pagina from '../pagine.module.css'
import styles from './Leads.module.css'

interface RispostaLista {
  leads: LeadAdmin[]
  totale: number
  page: number
  per_pagina: number
}

/**
 * Gestione lead di sistema (superadmin).
 *
 * Lista paginata di tutti i lead profile (pubblici + privati), con ricerca
 * e filtro difficoltà. Bottone "Nuovo lead" apre il drawer LeadForm.
 *
 * I lead con sessioni storiche non vengono cancellati ma "soft-delete"
 * (is_public=false) — il backend gestisce questo automaticamente.
 */
export function AdminLeads() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [cerca, setCerca] = useState('')
  const [filtroDiff, setFiltroDiff] = useState<number | ''>('')
  const [soloPubblici, setSoloPubblici] = useState(false)

  const [drawerAperto, setDrawerAperto] = useState(false)
  const [leadInModifica, setLeadInModifica] = useState<LeadAdmin | null>(null)
  const [leadInEliminazione, setLeadInEliminazione] = useState<LeadAdmin | null>(null)
  const [erroreElim, setErroreElim] = useState<string | null>(null)

  // Carichiamo le voci ElevenLabs subito per poter mostrare i nomi
  // accanto a ogni lead (invece di voice_id criptico).
  const { voci, caricaVoci, trovaNomeVoce } = useVociElevenLabs()
  useMemo(() => { void caricaVoci() }, [caricaVoci])

  const q = useQuery({
    queryKey: ['admin', 'leads', page, cerca, filtroDiff, soloPubblici],
    queryFn: async () => {
      const params: Record<string, unknown> = { page, per_pagina: 25 }
      if (cerca.trim()) params.cerca = cerca.trim()
      if (filtroDiff !== '') params.difficolta = filtroDiff
      if (soloPubblici) params.solo_pubblici = true
      return (await api.get<{ dati: RispostaLista }>('/superadmin/leads', { params })).data.dati
    },
  })

  const elimina = useMutation({
    mutationFn: async (id: string) => api.delete<{ dati: { soft_delete: boolean } }>(`/superadmin/leads/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
      setLeadInEliminazione(null)
    },
    onError: (err) => setErroreElim(messaggioErrore(err)),
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento lead di sistema…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  const lista = q.data.leads
  const totalePagine = Math.max(1, Math.ceil(q.data.totale / q.data.per_pagina))

  return (
    <div className={pagina.wrap}>
      {/* Intestazione */}
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Lead di Sistema</h1>
          <div className={pagina.sottotitolo}>
            Profili lead globali visibili a tutti gli utenti del portale.
          </div>
        </div>
        <Button
          iconaSinistra={<Plus size={16} />}
          onClick={() => { setLeadInModifica(null); setDrawerAperto(true) }}
        >
          Nuovo lead
        </Button>
      </div>

      {/* Filtri */}
      <div className={pagina.filtri}>
        <div className={pagina.search}>
          <Search size={16} color="var(--text-muted)" />
          <input
            type="text"
            placeholder="Cerca per nome, ruolo, settore…"
            value={cerca}
            onChange={(e) => { setCerca(e.target.value); setPage(1) }}
          />
        </div>
        <select
          className={pagina.select}
          value={filtroDiff}
          onChange={(e) => { setFiltroDiff(e.target.value ? parseInt(e.target.value, 10) : ''); setPage(1) }}
        >
          <option value="">Tutte le difficoltà</option>
          <option value={1}>Facile</option>
          <option value={2}>Medio</option>
          <option value={3}>Difficile</option>
        </select>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={soloPubblici}
            onChange={(e) => { setSoloPubblici(e.target.checked); setPage(1) }}
          />
          <span>Solo pubblici</span>
        </label>
      </div>

      {/* Lista lead */}
      {lista.length === 0 ? (
        <EmptyState
          icona={<UserRound size={40} />}
          titolo="Nessun lead trovato"
          descrizione="Crea il primo lead di sistema cliccando 'Nuovo lead'."
          azione={
            <Button onClick={() => { setLeadInModifica(null); setDrawerAperto(true) }}>
              Crea il primo lead
            </Button>
          }
        />
      ) : (
        <div className={styles.lista}>
          {lista.map((l) => (
            <Card key={l.id}>
              <div className={styles.cardCorpo}>
                <Avatar nome={l.nome} />
                <div className={styles.cardCentrale}>
                  <div className={styles.cardNome}>
                    {l.nome}
                    {!l.isPublic && <Badge variante="warning">Privato</Badge>}
                  </div>
                  <div className={styles.cardMeta}>
                    {l.ruolo} · {l.settore}
                  </div>
                  <div className={styles.cardBadges}>
                    <Badge variante={varianteDifficolta(l.difficolta)}>
                      {etichettaDifficolta(l.difficolta)}
                    </Badge>
                    {l.interno != null && (
                      <span className={pagina.tag}>Interno {l.interno}</span>
                    )}
                    <span className={styles.voce}>
                      <Volume2 size={12} />
                      {l.voiceId
                        ? (trovaNomeVoce(l.voiceId)
                          ?? (voci.length === 0
                            ? <code className={styles.voiceCode}>{l.voiceId.slice(0, 8)}…</code>
                            : <Badge variante="warning">Voce non trovata</Badge>))
                        : <span>Voce default</span>}
                    </span>
                  </div>
                </div>
                <div className={styles.cardAzioni}>
                  <Button
                    variante="secondary"
                    taglia="sm"
                    iconaSinistra={<Edit3 size={13} />}
                    onClick={() => { setLeadInModifica(l); setDrawerAperto(true) }}
                  >
                    Modifica
                  </Button>
                  <Button
                    variante="ghost"
                    taglia="sm"
                    onClick={() => { setLeadInEliminazione(l); setErroreElim(null) }}
                  >
                    <Trash2 size={13} color="var(--danger)" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Paginazione */}
      {totalePagine > 1 && (
        <div className={pagina.paginazione}>
          <span>Pagina {q.data.page} di {totalePagine} — {q.data.totale} lead totali</span>
          <div className={pagina.paginazioneBottoni}>
            <Button taglia="sm" variante="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} iconaSinistra={<ChevronLeft size={14} />}>
              Indietro
            </Button>
            <Button taglia="sm" variante="secondary" disabled={page >= totalePagine} onClick={() => setPage(page + 1)} iconaDestra={<ChevronRight size={14} />}>
              Avanti
            </Button>
          </div>
        </div>
      )}

      {/* Drawer creazione/modifica */}
      <LeadForm
        aperto={drawerAperto}
        lead={leadInModifica}
        onChiudi={() => setDrawerAperto(false)}
        onSalvato={() => setDrawerAperto(false)}
      />

      {/* Modale conferma eliminazione */}
      <Modal
        aperto={!!leadInEliminazione}
        titolo="Eliminare il lead?"
        onChiudi={() => setLeadInEliminazione(null)}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
          Sei sicuro di voler eliminare il lead <strong>{leadInEliminazione?.nome}</strong>?
          Se esistono sessioni storiche con questo lead, verrà disattivato (nascosto agli utenti)
          invece di essere eliminato definitivamente, per non spezzare lo storico.
        </p>
        {erroreElim && (
          <div style={{ padding: 10, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', fontSize: 13, marginBottom: 12 }}>
            {erroreElim}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variante="ghost" onClick={() => setLeadInEliminazione(null)}>Annulla</Button>
          <Button
            variante="danger"
            caricamento={elimina.isPending}
            onClick={() => leadInEliminazione && elimina.mutate(leadInEliminazione.id)}
          >
            Elimina
          </Button>
        </div>
      </Modal>
    </div>
  )
}

// ─── Avatar a iniziali con colore deterministico ────────────────
function Avatar({ nome }: { nome: string }) {
  const iniziali = nome
    .split(/\s+/)
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'
  // Hash semplice per scegliere un colore deterministico
  let h = 0
  for (let i = 0; i < nome.length; i++) h = (h * 31 + nome.charCodeAt(i)) >>> 0
  const sfumature = ['#6C47FF', '#16A34A', '#2563EB', '#D97706', '#DC2626', '#0EA5E9']
  const colore = sfumature[h % sfumature.length]
  return (
    <div className={styles.avatar} style={{ background: colore }}>{iniziali}</div>
  )
}
